// Updated AddProduct.jsx with AI API integration for description, FAQs, and ProductMedia
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, FormProvider, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Form,
  Spinner,
  Modal,
} from "react-bootstrap";
import Loader from "../../../components/common/Loader.jsx";
import {
  calculatePrice,
  clearAiState,
  clearProductDetail,
  createOffers,
  fetchCategories,
  fetchOfferDetails,
  fetchSpecifications,
  fetchSubcategoriesById,
  generateAiDescription,
  generateAiFaqs,
  updateOffers,
  aiEditImage,
  fetchMetalCode,
} from "../../../features/buyer/buyerSlice.js";
import productValidationSchema from "./sections/ValidationSchema.js";
import BasicDetails from "./sections/BasicDetails.jsx";
import TechnicalSpecifications from "./sections/TechnicalSpecifications.jsx";
import ProductDescription from "./sections/ProductDescription.jsx";
import ShippingTerms from "./sections/ShippingTerms.jsx";
import PricingEstimation from "./sections/PricingEstimation.jsx";
import ProductMedia from "./sections/ProductMedia.jsx";
import Specifications from "./sections/Specifications.jsx";
import FAQs from "./sections/FAQs.jsx";
import AppToast from "../../../components/seller/AppToast.jsx";
import { editImage } from "../../../features/user/userSlice.js";
import OfferPricing from "./sections/OfferPricing.jsx";
import productDraftSchema from "./sections/productDraftSchema.js";
import AppButton from "../../../components/buyer/AppButton.jsx";
import AppLoader from "../../../components/seller/AppLoader.js";


const AddOffer = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { detail: offerToEdit } = useSelector((state) => state.buyer.offers);
  const {
    description: aiDescription,
    faqs: aiFaqs,
    generatingDescription,
    generatingFaqs,
  } = useSelector((state) => state.buyer.ai);

  const { editImageLoading, editImageError, editImageSuccess } = useSelector(
    (state) => state.user
  );

  const TermsAndConditionsURL =
    "https://scraptrade.com.au/pages/slug/terms-and-conditions-scraptrade-com-au";
  const metalCode = useSelector((state) => state.buyer.metalCode);
  const categories = useSelector((s) => s.buyer.offer.categories.list);
  const subCategories = useSelector((s) => s.buyer.offer.subcategories.list);
  const subCategoriesLoading = useSelector((s) => s.buyer.offer.subcategories.loading);
  // console.log("Subcategories from Redux:", subCategories);
  const { kycList } = useSelector((s) => s.user);
  const kycType = kycList?.[0]?.kyc_type;
  const [uploadedImage, setUploadedImage] = useState(""); // for existing image storage
  const [imagePreview, setImagePreview] = useState(""); // for new selection preview
  const [videoPreview, setVideoPreview] = useState(""); // for main video.
  const formatBidDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:mm"
  };

  const [isDraft, setIsDraft] = useState(false); // Added for consistency with Seller
  const [useDraftSchema, setUseDraftSchema] = useState(false);

  const schema = React.useMemo(() => {
    return useDraftSchema
      ? productDraftSchema({ kycType })
      : productValidationSchema({ kycType, categories });
  }, [useDraftSchema, kycType, categories]);

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      category_id: "",
      subcategory_id: "",
      name: "",
      description: "",
      unit_of_measurement: "",
      // price: "",
      quantity: "",
      location: "",
      // condition: "Used",
      incoterms: "",
      payment_terms: "",
      lead_time: "",
      delivery_port: "",

      // Specifications & FAQs arrays
      specifications: [],
      faqs: [],

      // Offer pricing
      max_buying_price: "",
      discount_percentage: "",

      // Metal & waste-related
      metal_code: "",
      sorting_type: "",
      attachment: "", // renamed from attachment_waste
      waste_percentage: "",
      waste_range: "",
      toxic_level: "",
      hazard_types: [], // renamed from hazard_type
      hazard_description: "",

      // Pricing & measurement
      price_per_unit: "",
      // weight: "",
      currency: "",
      estimated_price: "",
      calc_unit: "",
      calc_weight: "",

      // Media
      image: null,
      video: null,

      save_as_draft: 0,
      disclaimer: false,
    },
  });

  const { handleSubmit, reset, setValue, watch } = methods;
  const [toast, setToast] = useState({
    visible: false,
    type: "info",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [draftSubmitting, setDraftSubmitting] = useState(false);
  const [showImagePrompt, setShowImagePrompt] = useState(false);


  const selectedCategoryId = useWatch({
    control: methods.control,
    name: "category_id",
  });

  const isMetal = React.useMemo(() => {
    if (!selectedCategoryId || !categories || !Array.isArray(categories))
      return false;
    const cat = categories.find(
      (c) => String(c.id) === String(selectedCategoryId)
    );
    if (!cat) return false;
    const name = (cat.name || "").toLowerCase();
    // Show PricingEstimation for Non-Ferrous AND Precious Metal
    // Only hide it for Ferrous Metal
    return (
      name.includes("non-ferrous") ||
      name.includes("non ferrous") ||
      name.includes("precious")
    );
  }, [selectedCategoryId, categories]);

  // Fetch initial data
  useEffect(() => {
    dispatch(clearAiState());
    dispatch(fetchCategories());
    if (id) {
      dispatch(fetchOfferDetails(id))
        .unwrap()
        .then((p) => {
          const mapped = {
            id: p.id ?? "",
            category_id: p.category_id ?? p.category?.id ?? "",
            subcategory_id: p.subcategory_id ?? p.subcategory?.id ?? "",
            name: p.name ?? "",
            description: p.description ?? "",
            price: p.price ?? "",
            quantity: p.quantity ?? "",
            location: p.location ?? "",
            unit_of_measurement: p.unit_of_measurement ?? "",
            incoterms: p.incoterms ?? p.product?.incoterms ?? "",
            payment_terms: p.payment_terms ?? p.product?.payment_terms ?? "",
            lead_time: p.lead_time ?? p.product?.lead_time ?? "",
            delivery_port: p.delivery_port ?? p.product?.delivery_port ?? "",
            metal_code: p.metal_code ?? p.product?.metal_code ?? "",
            sorting_type: p.sorting_type ?? p.product?.sorting_type ?? "",
            attachment: p.attachment ?? p.product?.attachment ?? "",
            waste_range: p.waste_range ?? p.product?.waste_range ?? "",
            toxic_level: p.toxic_level ?? "",
            hazard_types: p.hazard_types ?? [],
            hazard_description: p.hazard_description ?? "",
            specifications: Array.isArray(p.specifications)
              ? p.specifications.map((s) => ({
                field_id: s.field?.id ?? s.specification_field_id,
                label: s.field?.name ?? s.label ?? "",
                input_type: s.field?.input_type ?? "text",
                value: s.value ?? "",
              }))
              : [],
            faqs: p.faqs ?? [],
            weight: p.weight ?? p.product?.weight ?? "",

            // Offer Pricing
            max_buying_price: p.max_buying_price ?? p.product?.max_buying_price ?? "",
            discount_percentage: p.discount_percentage ?? p.product?.discount_percentage ?? "",

            // --- Bidding Fields ---
            // is_reverse_auction: p.is_reverse_auction ?? false,
            // allow_bidding: Number(p.allow_bidding ?? 1), // Default to 1 if not available
            // minimum_asking_price:
            //   p.allow_bidding === 0 ? null : p.minimum_asking_price ?? "",
            // bid_increment: p.allow_bidding === 0 ? null : p.bid_increment ?? "",
            // bid_closed_at:
            //   p.allow_bidding === 0 ? null : formatBidDate(p.bid_closed_at),
            // auction_status:
            //   p.allow_bidding === 0 ? "closed" : p.auction_status ?? "open",

            // --- Pricing ---
            price_per_unit: p.price_per_unit ?? "",
            estimated_price: p.estimated_price ?? "",
            currency: p.currency || "",

            // --- Media ---
            image: null,
            existingImage: p.image_url || "",
            video: null,

            // --- Previews ---
            aiPreview: p.ai_image_url ?? p.product?.ai_image_url ?? "",
          };
          reset(mapped);
          // setValue("specifications", mapped.specifications);
          // set previews after reset
          setUploadedImage(p.image_url || "");
          setImagePreview(""); // keep blank for comparison
          setVideoPreview(p.video_url || "");
        });
    }
  }, [id, dispatch, reset]);

  // Handle category/subcategory dependent data
  const categoryValue = watch("category_id");
  useEffect(() => {
    if (categoryValue) dispatch(fetchSubcategoriesById(categoryValue));
    // console.log('category_id', categoryValue);
  }, [categoryValue, dispatch]);

  const subcategoryValue = watch("subcategory_id");
  useEffect(() => {
    if (subcategoryValue && !id) {
      setValue("specifications", []);
      dispatch(fetchSpecifications(subcategoryValue))
        .unwrap()
        .then((specs) => {
          const formatted = specs.map((spec) => ({
            field_id: spec.id,
            label: spec.name,
            input_type: spec.input_type || "text",
            value: "",
          }));
          setValue("specifications", formatted);
        });
    }
  }, [subcategoryValue, dispatch, setValue]);

  useEffect(() => {
    if (categoryValue && subcategoryValue) {
      dispatch(
        fetchMetalCode({
          category_id: categoryValue,
          subcategory_id: subcategoryValue,
        })
      )
        .unwrap()
        .then((res) => {
          if (res?.metal_code) {
            setValue("metal_code", res.metal_code);
          } else if (typeof res === "string") {
            // Some APIs just return the code string directly
            setValue("metal_code", res);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch metal code:", err);
          setToast({
            visible: true,
            type: "error",
            message: "Failed to fetch metal code",
          });
        });
    }
  }, [categoryValue, subcategoryValue, dispatch, setValue]);

  // Update previews when files change
  const watchedImage = watch("image");
  const watchedVideo = watch("video");

  useEffect(() => {
    if (watchedImage?.[0])
      setImagePreview(URL.createObjectURL(watchedImage[0]));
  }, [watchedImage]);

  useEffect(() => {
    if (watchedVideo?.[0])
      setVideoPreview(URL.createObjectURL(watchedVideo[0]));
  }, [watchedVideo]);

  // const allow = Number(watch("allow_bidding"));

  // useEffect(() => {
  //   if (allow === 0) {
  //     methods.clearErrors([
  //       "minimum_asking_price",
  //       "bid_increment",
  //       "bid_closed_at",
  //       "currency",
  //       "auction_status",
  //     ]);

  //     // Optionally also clear their values
  //     setValue("minimum_asking_price", null);
  //     setValue("bid_increment", null);
  //     setValue("bid_closed_at", null);
  //     setValue("currency", "USD");
  //   }
  // }, [allow]);

  // AI Handlers
  const handleAiDescription = async () => {
    console.log("🔥 HANDLE AI DESCRIPTION CLICKED");

    const name = watch("name");
    const category_id = watch("category_id");
    const unit_of_measurement = watch("unit_of_measurement");
    const location = watch("location");

    console.log("category_id from form:", category_id);

    if (!name) {
      return setToast({
        visible: true,
        type: "info",
        message: "Enter product name first.",
      });
    }

    if (!category_id) {
      return setToast({
        visible: true,
        type: "info",
        message: "Please select category first.",
      });
    }

    const selectedCategory = categories.find(
      (c) => String(c.id) === String(category_id)
    );

    console.log("matched category:", selectedCategory);

    const payload = {
      name,
      category: selectedCategory?.name, //  now works
      category_id, //  include id too
      condition: "Used",
      quantity: 1,
      unit: unit_of_measurement || "kg",
      location: location || "",
    };

    console.log("🚀 FINAL AI PAYLOAD:", payload);

    dispatch(generateAiDescription(payload));
  };

  useEffect(() => {
    if (aiDescription) setValue("description", aiDescription);
  }, [aiDescription, setValue]);

  const handleAiFaqs = async () => {
    const name = watch("name");
    const category_id = watch("category_id");
    const weight = watch("weight");
    const unit_of_measurement = watch("unit_of_measurement");
    const location = watch("location");

    console.log("name:", name);
    if (!name)
      return setToast({
        visible: true,
        type: "info",
        message: "Enter product name first.",
      });

    const payload = {
      name,
      category: categories.find((c) => c.id === category_id)?.name || "",
      condition: "Used",
      quantity: weight || 1,
      unit: unit_of_measurement || "kg",
      location: location || "",
    };
    dispatch(generateAiFaqs(payload));
  };

  useEffect(() => {
    if (aiFaqs?.length) setValue("faqs", aiFaqs);
  }, [aiFaqs, setValue]);

  // const handleAiImageEdit = async (action) => {
  //   const imageFile = watch("image")?.[0];

  //   if (!imageFile) {
  //     return setToast({
  //       visible: true,
  //       type: "info",
  //       message: "Upload an image first.",
  //     });
  //   }

  //   const formData = new FormData();
  //   formData.append("image", imageFile);
  //   formData.append("action", action);

  //   const result = await dispatch(editImage(formData)).unwrap();
  //   console.log("🧠 AI IMAGE RESPONSE:", result);

  //   const editedUrl = result?.data?.image_base64;
  //   if (!editedUrl) return;

  //   //  SAME AS OLD CODE — convert URL → File
  //   const resp = await fetch(editedUrl);
  //   const blob = await resp.blob();
  //   const aiFile = new File([blob], "ai-edited.png", { type: blob.type });

  //   //  replace image in react-hook-form
  //   setValue("image", [aiFile], {
  //     shouldDirty: true,
  //     shouldTouch: true,
  //   });

  //   //  update preview
  //   setImagePreview(editedUrl);
  // };




  const handleAiImageEdit = async (action) => {
    const imageFile = watch("image")?.[0];

    if (!imageFile) {
      return setToast({
        visible: true,
        type: "info",
        message: "Upload an image first.",
      });
    }

    const formData = new FormData();
    formData.append("image", imageFile);
    formData.append("action", action);

    const result = await dispatch(editImage(formData)).unwrap();

    const editedBase64 = result?.data?.image_base64;
    if (!editedBase64) return;

    // ✅ SET AI PREVIEW (THIS WAS MISSING)
    setValue("aiPreview", editedBase64, {
      shouldDirty: true,
    });

    // ❌ DO NOT update imagePreview
    // ❌ DO NOT replace original image
  };


  const handleSaveAsDraft = () => {
    const data = watch();

    // Make sure these fields are filled out before saving as draft
    const isDraftable =
      data.name && data.category_id && data.subcategory_id && data.location;

    if (!isDraftable) {
      return setToast({
        visible: true,
        type: "info",
        message:
          "Please fill out the product name, category, subcategory, and location before saving as draft.",
      });
    }
    setUseDraftSchema(true);
    // if (isDraftable) {
    //   setIsDraft(true);
    //   setCurrentSchema(productDraftSchema);

    //   // Set the save_as_draft flag to 1 (draft)
    //   setTimeout(() => onSubmit(data, true), 1200);
    // } else {
    //   setToast({
    //     visible: true,
    //     type: "info",
    //     message:
    //       "Please fill out the product name, category, subcategory, and location before saving as draft.",
    //   });
    // }
  };

  const onSubmit = async (data, isDraftMode = false) => {
    if (!isDraftMode) setSubmitting(true);

    try {
      // const isDraftMode = isDraft === true;
      // Remove existingImage, backend handles media
      const payload = { ...data };
      payload.currency = data.currency || "AUD";

      // payload.save_as_draft = isDraftMode ? 1 : 0;
      console.log("Final Draft Flag:", payload.save_as_draft);
      // Clean empty strings or undefined values from payload
      Object.keys(payload).forEach((key) => {
        if (payload[key] === "" || payload[key] === undefined) {
          payload[key] = null; // Convert empty string or undefined to null
        }
      });

      if (isDraftMode) {
        // setSubmitting(true);
        Object.keys(payload).forEach((key) => {
          if (
            payload[key] === null ||
            payload[key] === undefined ||
            payload[key] === ""
          ) {
            delete payload[key]; // Delete fields that are empty or undefined
          }
        });
        // Optional: Remove fields that backend may reject when empty
        // delete payload.category_id;
        // delete payload.subcategory_id;
        // delete payload.minimum_asking_price;
        // delete payload.bid_increment;
        // But keep whatever user filled in
      }
      // payload.allow_bidding = Number(payload.allow_bidding);
      // if (payload.allow_bidding === 0) {
      //   payload.minimum_asking_price = null;
      //   payload.bid_increment = null;
      //   payload.bid_closed_at = null;
      //   payload.auction_status = null; // auction_status might be omitted or set to a default value like 'closed'
      // }


      const base64ToFile = async (base64, filename = "ai-image.png") => {
        const res = await fetch(base64);
        const blob = await res.blob();

        console.log("Blob from base64:", blob);
        console.log("Filename:", filename);
        return new File([blob], filename, { type: blob.type });
      };

      // Keep image as array for service layer OR convert AI preview to array
      if (data.aiPreview) {
        const aiFile = await base64ToFile(data.aiPreview);
        payload.image = [aiFile]; // Wrap in array
      } else {
        payload.image = data.image; // Keep as FileList/array
      }

      delete payload.existingImage;
      delete payload.aiPreview;
      delete payload.video_url;

      console.log("FINAL PAYLOAD BEFORE API:", payload);

      const response = id
        ? await dispatch(updateOffers({ id, data: payload })).unwrap()
        : await dispatch(createOffers(payload)).unwrap();
      console.log("Message:", response);
      const successMsg = response?.message || "Saved";
      navigate("/my-offers", { state: { toast: { type: "success", message: successMsg } } });
    } catch (err) {
      console.error("Submission error:", err);

      if (err.errors) console.table(err.errors);

      setToast({
        visible: true,
        type: "error",
        message:
          Object.values(err.errors || {})[0]?.[0] ||
          err.message ||
          "Failed to save",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveClick = async () => {
    // Set draft flag to 0 for normal save
    methods.setValue("save_as_draft", 0);
    setUseDraftSchema(false);

    const image = watch("image");
    const existingImage = watch("existingImage");
    const aiPreview = watch("aiPreview");

    // Check if any image is provided
    const hasNoImage = (!image || image.length === 0) && !existingImage && !aiPreview;

    const handleFormErrors = (formErrors) => {
      console.error("Validation errors:", formErrors);
      setToast({
        visible: true,
        type: "error",
        message: "Validation failed. Check for missing fields.",
      });
    };

    if (hasNoImage) {
      setShowImagePrompt(true);
    } else {
      // Use handleSubmit with error handler to validate and submit
      handleSubmit(onSubmit, handleFormErrors)();
    }
  };

  return (
    <Container fluid className="px-3">
      <div className="form-header-bar mb-2">
        <h1>Create Buyer Offer (Reverse Bidding)</h1>
        <button className="back-button" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>

      <Card className="border-0 shadow-none">
        <Card.Body>
          <FormProvider {...methods}>
            <Form
              onSubmit={handleSubmit(onSubmit, (formErrors) => {
                console.error("Validation errors:", formErrors);
                setToast({
                  visible: true,
                  type: "error",
                  message: "Validation failed. Check for missing fields.",
                });
              })}
            >
              <BasicDetails
                categories={categories}
                subCategories={subCategories}
                setValue={setValue}
              />
              <TechnicalSpecifications isMetal={isMetal} />
              <ProductDescription
                onGenerateAi={handleAiDescription}
                aiLoading={generatingDescription}
              />
              <Specifications />
              {isMetal && (
                <PricingEstimation
                  subCategories={subCategories}
                  calculatePrice={async (payload) => {
                    try {
                      const resultAction = await dispatch(
                        calculatePrice(payload)
                      ).unwrap();
                      return resultAction;
                    } catch (err) {
                      throw err;
                    }
                  }}
                />
              )}
              <OfferPricing isMetal={isMetal} />
              <ShippingTerms />
              <ProductMedia
                onAiEdit={handleAiImageEdit}
                aiLoading={editImageLoading}
                uploadedImage={uploadedImage}
                imagePreview={imagePreview}
                setImagePreview={setImagePreview}
                videoPreview={videoPreview}
                setVideoPreview={setVideoPreview}
                kycType={kycType}
                isEditMode={!!id}
              />
              <FAQs onGenerateAi={handleAiFaqs} aiLoading={generatingFaqs} />

              <Row className="mt-4">
                <Col>
                  <div
                    style={{
                      padding: "12px 15px",
                      border: "1px solid #dcdcdc",
                      borderRadius: "6px",
                      background: "#f9f9f9",
                    }}
                  >
                    <div className="d-flex align-items-start">
                      <input
                        type="checkbox"
                        style={{ marginTop: "4px", marginRight: "10px" }}
                        {...methods.register("disclaimer", { required: true })}
                      />

                      <label style={{ fontSize: "0.9rem" }}>
                        {/* I confirm that all the information provided is true and
                        accurate to the best of my knowledge. I agree to the */}
                        By using this platform, I agree to the{" "}
                        <a
                          href={TermsAndConditionsURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontWeight: "600",
                            textDecoration: "underline",
                          }}
                        >
                          Terms & Conditions
                        </a>
                        .
                      </label>
                    </div>

                    {methods.formState.errors.disclaimer && (
                      <p
                        style={{
                          color: "red",
                          marginTop: "6px",
                          marginLeft: "26px",
                        }}
                      >
                        You must agree before submitting.
                      </p>
                    )}
                  </div>
                </Col>
              </Row>

              <Row className="mt-5 align-items-center">
                <Col className="text-start">
                  <AppButton
                    variant="outlined"
                    fullWidth={false}
                    disabled={
                      !watch("name") ||
                      !watch("category_id") ||
                      !watch("subcategory_id") ||
                      !watch("location")
                    }
                    onClick={async () => {
                      setDraftSubmitting(true);

                      methods.setValue("save_as_draft", 1);
                      const data = methods.getValues();

                      await onSubmit(data, true);

                      setDraftSubmitting(false);
                    }}
                    style={{ minWidth: 160 }}
                  >
                    Save as Draft
                  </AppButton>
                </Col>

                <Col className="text-end d-flex justify-content-end gap-2">
                  <AppButton
                    type="button"
                    variant="contained"
                    fullWidth={false}
                    disabled={submitting || !watch("disclaimer")}
                    onClick={handleSaveClick}
                    style={{ minWidth: 120 }}
                  >
                    {submitting ? "Saving..." : "Save"}
                  </AppButton>

                  <AppButton
                    variant="outlined"
                    fullWidth={false}
                    onClick={() => navigate(-1)}
                    style={{ minWidth: 120 }}
                  >
                    Cancel
                  </AppButton>
                </Col>
              </Row>
            </Form>
          </FormProvider>
        </Card.Body>
      </Card>

      <AppToast
        visible={toast.visible}
        setVisible={(v) => setToast((s) => ({ ...s, visible: v }))}
        type={toast.type}
        message={toast.message}
      />
      {subCategoriesLoading && <AppLoader fullScreen />}

      {/* Image Encouragement Modal */}
      <Modal
        show={showImagePrompt}
        onHide={() => setShowImagePrompt(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Better Reach Tip</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            You haven't added an image to your offer yet.
            Offers with clear images have <b>better reach</b> and are more likely to attract interest.
          </p>
          <p>Are you sure you want to proceed without an image?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImagePrompt(false)}>
            Wait, let me upload
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowImagePrompt(false);
              // Use handleSubmit to ensure validation passes before onSubmit
              // Re-use the same error handling logic
              handleSubmit(onSubmit, (formErrors) => {
                console.error("Validation errors (from prompt):", formErrors);
                setToast({
                  visible: true,
                  type: "error",
                  message: "Validation failed. Complete other fields first.",
                });
              })();
            }}
          >
            Yes, Save anyway
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AddOffer;
